import { useEffect, useId, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { theme } from '~/shared/styles/theme';

interface Props<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  placeholder: string;
}

function FilterTextWithAdd<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
}: Props<T>) {
  const id = useId();
  const inputId = `${id}-input`;
  const labelId = `${id}-label`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FilterTextWithAddField
          field={field}
          label={label}
          labelId={labelId}
          inputId={inputId}
          placeholder={placeholder}
        />
      )}
    />
  );
}

interface FieldProps {
  field: {
    value?: string[] | null;
    onChange: (value: string[] | undefined) => void;
  };
  label: string;
  labelId: string;
  inputId: string;
  placeholder: string;
}

function FilterTextWithAddField({
  field,
  label,
  labelId,
  inputId,
  placeholder,
}: FieldProps) {
  const [draft, setDraft] = useState('');
  const values = field.value ?? [];

  useEffect(() => {
    if (!field.value?.length) setDraft('');
  }, [field.value]);

  const handleAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    field.onChange([...values, trimmed]);
    setDraft('');
  };

  const handleRemove = (index: number) => {
    const next = values.filter((_, i) => i !== index);
    field.onChange(next.length ? next : undefined);
  };

  return (
    <Stack width="100%" sx={{ pb: 2 }}>
      <FilterLabel id={labelId} variant="body2">
        {label}
      </FilterLabel>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box flex={1}>
          <TextField
            id={inputId}
            slotProps={{ htmlInput: { 'aria-labelledby': labelId } }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder={placeholder}
            fullWidth
            size="small"
          />
        </Box>
        <AddButton type="button" onClick={handleAdd}>
          Add
        </AddButton>
      </Stack>
      {values.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          {values.map((value, index) => (
            <FilterChip
              key={`${value}-${index}`}
              direction="row"
              alignItems="center"
              gap={0.75}
            >
              <Typography variant="body2" sx={{ fontSize: 14 }}>
                {value}
              </Typography>
              <RemoveButton
                size="small"
                onClick={() => handleRemove(index)}
                aria-label={`Remove ${value}`}
              >
                ✕
              </RemoveButton>
            </FilterChip>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

const FilterLabel = styled(Typography)({
  color: theme.palette.custom.cohortColumnHeader,
  fontSize: 12,
  marginBottom: theme.spacing(1),
});

const AddButton = styled(Button)(() => ({
  height: 40,
  borderRadius: 10,
  boxShadow: 'inset 0 -2px 0 0 rgba(27, 50, 42, 0.5)',
  backgroundColor: theme.palette.custom.cohortColumnHeader,
  color: theme.palette.common.white,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: theme.palette.custom.cohortColumnHeader,
    boxShadow: 'inset 0 -2px 0 0 rgba(27, 50, 42, 0.7)',
  },
}));

const FilterChip = styled(Stack)({
  borderRadius: 6,
  backgroundColor: theme.palette.action.hover,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
});

const RemoveButton = styled(IconButton)({
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.error.main,
  },
});

export default FilterTextWithAdd;
